import type { Settings } from 'sigma/settings';
import type { NodeDisplayData, PartialButFor } from 'sigma/types';

import { ENTITY_COLORS } from '../../Types/graph';

const TEXT_COLOR = '#000000';

export const safeTypeColor = (t: string | undefined): string => {
  if (!t) return '#999';
  return ENTITY_COLORS[t] ?? '#999';
};

export function drawRoundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
): void {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

export function drawHover(
  context: CanvasRenderingContext2D,
  data: Record<string, unknown>,
  settings: Settings
): void {
  const size = settings.labelSize;
  const font = settings.labelFont;
  const weight = settings.labelWeight;
  const subLabelSize = size - 2;

  const label = (data.label as string) || '';
  const entityType = (data.entityType as string) || '';

  context.beginPath();
  context.fillStyle = '#fff';
  context.shadowOffsetX = 0;
  context.shadowOffsetY = 2;
  context.shadowBlur = 8;
  context.shadowColor = '#000';

  // Measure text widths
  context.font = `${weight} ${size}px ${font}`;
  const labelWidth = context.measureText(label).width;
  context.font = `${weight} ${subLabelSize}px ${font}`;
  const typeWidth = entityType ? context.measureText(entityType).width : 0;

  const textWidth = Math.max(labelWidth, typeWidth);

  const x = Math.round(data.x as number);
  const y = Math.round(data.y as number);
  const nodeSize = (data.size as number) || 1;
  const w = Math.round(textWidth + size / 2 + nodeSize + 3);
  const hLabel = Math.round(size / 2 + 4);
  const hType = entityType ? Math.round(subLabelSize / 2 + 9) : 0;

  // Draw background
  drawRoundRect(context, x, y - 12, w, hLabel + hType + 12, 5);
  context.closePath();
  context.fill();

  // Reset shadow
  context.shadowOffsetX = 0;
  context.shadowOffsetY = 0;
  context.shadowBlur = 0;

  // Draw main label
  context.fillStyle = TEXT_COLOR;
  context.font = `${weight} ${size}px ${font}`;
  context.fillText(label, (data.x as number) + nodeSize + 3, (data.y as number) + size / 3);

  // Draw entity type below
  if (entityType) {
    context.fillStyle = (data.color as string) || '#666';
    context.font = `${weight} ${subLabelSize}px ${font}`;
    context.fillText(
      entityType,
      (data.x as number) + nodeSize + 3,
      (data.y as number) + size / 3 + 3 + subLabelSize
    );
  }
}

export function drawLabel(
  context: CanvasRenderingContext2D,
  data: PartialButFor<NodeDisplayData, 'x' | 'y' | 'size' | 'label' | 'color'>,
  settings: Settings
): void {
  if (!data.label) return;

  const size = settings.labelSize;
  const font = settings.labelFont;
  const weight = settings.labelWeight;

  context.font = `${weight} ${size}px ${font}`;
  const width = context.measureText(data.label).width + 8;

  // Draw label background for better readability
  context.fillStyle = '#ffffffcc';
  context.fillRect(data.x + data.size, data.y + size / 3 - 15, width, 20);

  // Draw label text
  context.fillStyle = '#000';
  context.fillText(data.label, data.x + data.size + 3, data.y + size / 3);
}
